import { defaultParser, Token, TokenType } from "@theprelude/odata-parser";
import { z } from "zod";
import {
  AdditionalFilter,
  flipOperator,
  getOdataDurationString,
  prefixDurationId,
} from "@/components/filter-form/additional-filters";
import {
  NEGATED_OPERATOR_ODATA_METHOD,
  ODataMethod,
  OPERATOR_ODATA_METHOD,
} from "@/components/filter-form/odata";
import { Filter } from "./filter";
import {
  partnerEventSchema,
  validControlCategorySchema,
  validControlSchema,
} from "./validators";

export const ODATA_OPERATORS = [
  "contains",
  "matches",
  "does not match",
  "greater than",
  "less than",
  "is",
  "is not",
] as const;

export const odataPropertyFns = ["length"] as const;
type OdataPropertyFn = (typeof odataPropertyFns)[number];

export const odataScopes = ["instances", "failures", "excepted"] as const;
export const odataOperatorSchema = z.enum(ODATA_OPERATORS);
export const odataObjectValueSchema = z.object({
  type: z.enum(["guid", "duration", "binary", "decimal", "raw"]),
  value: z.string().nonempty(),
});
export type OdataObjectValue = z.infer<typeof odataObjectValueSchema>;
export const odataValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  odataObjectValueSchema,
]);
export const odataDefinitionSchema = z.object({
  id: z.string(),
  scope: z.enum(odataScopes).optional(),
  with: z
    .object({
      instance_id: z.string().optional(),
      category: validControlCategorySchema.or(z.null()).optional(),
      control: validControlSchema.optional(),
      event: partnerEventSchema.optional(),
    })
    .optional(),
  sort: z.string().or(z.array(z.string())).optional(),
});

export type OdataValue = z.infer<typeof odataValueSchema>;
export type OdataOperator = z.infer<typeof odataOperatorSchema>;
export type OdataDefinition = z.infer<typeof odataDefinitionSchema>;
export type ObjectFilter = Omit<Filter, "value"> & { value: OdataObjectValue };

const propertyFnRegex = new RegExp(
  `^(${Object.values(odataPropertyFns).join("|")})\\((.+)\\)$`,
);

export function isOdataControlId(id: string): boolean {
  return /\bcontrol=\d+\b/.test(id);
}

export function getOdataId(definition: OdataDefinition) {
  return [
    definition.scope,
    definition.id,
    ...(definition.with
      ? Object.entries(definition.with)
          .map(([key, value]) => `${key}=${value}`)
          .sort()
      : []),
  ]
    .filter(Boolean)
    .join(":");
}

export function parseOdataId(encodedOdataId: string) {
  return encodedOdataId.split(":")[1] ?? encodedOdataId;
}

export function getEncodedOdataValue(
  value: string | number | boolean | null,
): string {
  if (typeof value === "string") {
    return `'${value.replaceAll("'", "''")}'`;
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return "null";
}

export function isDurationFilterValue(value: Filter["value"]): boolean {
  return (
    typeof value === "object" &&
    value.type === "raw" &&
    value.value.includes("now() sub duration")
  );
}

export function isDurationFilter(filter: Filter): filter is ObjectFilter {
  return isDurationFilterValue(filter.value);
}

function parseFilterProperty(filter: Filter): Pick<Filter, "id" | "fn"> {
  const match = filter.id.match(propertyFnRegex);
  const id = match?.[2] ?? filter.id;
  const fn = match?.[1] as OdataPropertyFn | undefined;

  return {
    id: isDurationFilterValue(filter.value) ? prefixDurationId(id) : id,
    fn,
  };
}

export function parseFilter(
  source: string,
  availableFilters: AdditionalFilter[],
): Filter[] {
  const filters = new FilterParser(availableFilters)
    .parse(source)
    .map((filter) => {
      // This is necessary until support of seconds_in_state is no longer needed
      if (filter.id === "seconds_in_state") {
        return {
          ...filter,
          ...parseFilterProperty({ ...filter, id: "duration_start_time" }),
          operator: flipOperator(filter.operator),
          value: {
            type: "raw",
            value: `now() sub duration'${getOdataDurationString(filter.value)}'`,
          },
          ors: filter.ors || [],
        };
      }
      return { ...filter, ...parseFilterProperty(filter) };
    });

  return z
    .array(
      odataDefinitionSchema.extend({
        fn: z.enum(odataPropertyFns).optional(),
        operator: odataOperatorSchema,
        value: odataValueSchema,
        ors: z
          .array(
            z.object({
              operator: odataOperatorSchema,
              value: odataValueSchema,
            }),
          )
          .optional(),
      }),
    )
    .parse(filters);
}

class FilterParser {
  private filters: AdditionalFilter[];

  constructor(filters: AdditionalFilter[]) {
    this.filters = filters;
  }

  parse(source: string): Filter[] {
    const ast = defaultParser.filter(source);
    const result = this.visitToken(ast);
    return Array.isArray(result) ? result : [result];
  }

  private visitToken(token: Token): any {
    switch (token.type) {
      case TokenType.FirstMemberExpression: {
        if (Array.isArray(token.value)) {
          return this.visitToken(token.value.at(-1));
        }
        return this.visitToken(token.value);
      }
      case TokenType.PropertyPathExpression: {
        if (token.value instanceof Token) {
          return this.visitToken(token.value);
        }
        const current = this.visitToken(token.value.current);
        const next = this.visitToken(token.value.next);

        if (this.isScope(current)) {
          const scopeSchema = odataDefinitionSchema.shape.scope.unwrap();
          const parseResult = scopeSchema.safeParse(current);
          if (!parseResult.success) {
            throw new Error(
              `Invalid scope name '${current}' identified in PropertyPathExpression: ${token.raw}`,
            );
          }
          return this.getScopedFilter(parseResult.data, next);
        } else {
          const nextFilter = next as Filter;
          nextFilter.id = current;
          return nextFilter;
        }
      }
      case TokenType.ODataIdentifier: {
        return token.value.name as string;
      }
      case TokenType.LambdaVariableExpression: {
        return token.value.name as string;
      }
      case TokenType.AnyExpression: {
        return this.handleAnyExpression(token);
      }
      case TokenType.MethodCallExpression: {
        const method = token.value.method;
        const parameters =
          token.value.parameters?.map((t: Token) => this.visitToken(t)) ?? [];
        const methodOperator = ODATA_METHOD_OPERATORS[method];
        if (methodOperator) {
          return {
            id: parameters[0],
            operator: methodOperator,
            value: parameters[1],
            ors: [],
          } satisfies Filter;
        }
        return token.raw;
      }
      case TokenType.HasExpression:
      case TokenType.AddExpression:
      case TokenType.SubExpression:
      case TokenType.MulExpression:
      case TokenType.DivExpression:
      case TokenType.ModExpression: {
        const left = this.visitToken(token.value.left);
        const right = this.visitToken(token.value.right);
        const operator = token.type.slice(0, 3).toLowerCase();
        return { type: "raw", value: `${left} ${operator} ${right}` };
      }
      case TokenType.Literal: {
        return this.literal(token);
      }
      case TokenType.AndExpression: {
        return this.join(token.type, token.value.left, token.value.right);
      }
      case TokenType.OrExpression: {
        return this.join(token.type, token.value.left, token.value.right);
      }
      case TokenType.NotExpression: {
        return this.negate(this.visitToken(token.value));
      }
      case TokenType.GreaterThanExpression: {
        return this.getOperation(token, "greater than");
      }
      case TokenType.LesserThanExpression: {
        return this.getOperation(token, "less than");
      }
      case TokenType.EqualsExpression: {
        return this.getOperation(token, "is");
      }
      case TokenType.NotEqualsExpression: {
        return this.getOperation(token, "is not");
      }
      case TokenType.ParenExpression:
      case TokenType.BoolParenExpression:
      case TokenType.CommonExpression:
      case TokenType.MemberExpression:
      case TokenType.LambdaPredicateExpression:
      case TokenType.CollectionPathExpression: {
        return this.visitToken(token.value);
      }
      default: {
        throw new Error(`Unhandled token: ${token.type}`);
      }
    }
  }

  private handleAnyExpression(token: Token): Filter {
    const variable = this.visitToken(token.value.variable);
    const predicate = this.visitToken(token.value.predicate);

    if (this.isScope(variable)) {
      const scopeSchema = odataDefinitionSchema.shape.scope.unwrap();
      const parseResult = scopeSchema.safeParse(variable);
      if (!parseResult.success) {
        throw new Error(
          `Invalid scope name '${variable}' identified in AnyExpression: ${token.raw}`,
        );
      }
      return this.getScopedFilter(parseResult.data, predicate);
    } else {
      const predicateFilter = predicate as Filter;
      const resultFilter: Filter = {
        id: variable,
        operator: predicateFilter.operator,
        value: predicateFilter.value,
        isArray: true,
        ors:
          predicateFilter.ors && predicateFilter.ors.length > 0
            ? predicateFilter.ors
            : undefined,
      };
      return resultFilter;
    }
  }

  private getScopedFilter(
    scope: NonNullable<OdataDefinition["scope"]>,
    fieldFilter: Filter | Filter[],
  ): Filter {
    if (Array.isArray(fieldFilter)) {
      const filterField =
        fieldFilter.find((f) =>
          this.filters.some((filter) => filter.odata.id === f.id),
        ) ?? fieldFilter.at(-1);

      if (!filterField) {
        throw new Error(
          `No matching field for: ${fieldFilter.map((f) => f.id).join(", ")}`,
        );
      }

      const withFields = fieldFilter.filter((f) => f.id !== filterField.id);
      // eslint-disable-next-line unicorn/prefer-object-from-entries -- fromEntries does not preserve strict key typing
      const withs = withFields.reduce(
        (acc: OdataDefinitionWith, field) => ({
          ...acc,
          [field.id]: field.value,
        }),
        {},
      );

      const id = getOdataId({ scope, id: filterField.id, with: withs });
      const filter = this.filters.find((f) => f.id === id);
      if (!filter) {
        return { ...filterField, scope, with: withs };
      }
      return { ...filterField, ...filter.odata };
    }
    const id = getOdataId({ ...fieldFilter, scope });
    const filter = this.filters.find((f) => f.id === id);
    if (!filter) {
      return { ...fieldFilter, scope };
    }
    return { ...fieldFilter, ...filter.odata };
  }

  private getOperation(token: Token, operator: OdataOperator): Filter {
    const id = this.visitToken(token.value.left);
    const value = this.visitToken(token.value.right);
    return { id, operator, value, ors: [] } satisfies Filter;
  }

  private join(type: TokenType, a: Token, b: Token) {
    const left = this.visitToken(a);
    const right = this.visitToken(b);

    // Handle OR merging for the same field ID
    if (
      type === TokenType.OrExpression &&
      !Array.isArray(left) &&
      !Array.isArray(right) &&
      typeof left === "object" &&
      typeof right === "object" &&
      left.id === right.id &&
      left.id // ensure id exists
    ) {
      const leftFilter = left as Filter;
      const rightFilter = right as Filter;
      leftFilter.ors = [
        ...(leftFilter.ors || []),
        { operator: rightFilter.operator, value: rightFilter.value },
        ...(rightFilter.ors || []), // Merge any nested ors from the right side too
      ];
      return leftFilter;
    }

    // Default: Concatenate results into an array (for AND or OR on different fields)
    const result = [];
    if (Array.isArray(left)) {
      result.push(...left);
    } else {
      result.push(left);
    }
    if (Array.isArray(right)) {
      result.push(...right);
    } else {
      result.push(right);
    }
    return result;
  }

  private negate(filter: Filter): Filter {
    switch (filter.operator) {
      case "matches": {
        return { ...filter, operator: "does not match" };
      }
      case "is": {
        return { ...filter, operator: "is not" };
      }
      case "less than": {
        return { ...filter, operator: "greater than" };
      }
      case "greater than": {
        return { ...filter, operator: "less than" };
      }
      default: {
        throw new Error(`Unexpected negation: ${filter.operator}`);
      }
    }
  }

  private literal(token: Token) {
    switch (token.value) {
      case "Edm.String": {
        const match = token.raw.match(/^'(.+)'$/);
        return match?.[1] ?? token.raw;
      }
      case "Edm.Boolean": {
        return token.raw === "true";
      }
      case "Edm.Date": {
        return new Date(token.raw);
      }
      case "Edm.Byte":
      case "Edm.SByte":
      case "Edm.Decimal":
      case "Edm.Double":
      case "Edm.Int16":
      case "Edm.Int32":
      case "Edm.Int64": {
        return Number(token.raw);
      }
      default: {
        return token.raw;
      }
    }
  }

  private isScope(pathName: string): boolean {
    // eslint-disable-next-line unicorn/prefer-includes
    return odataScopes.some((scope) => scope === pathName);
  }
}

const ODATA_METHOD_OPERATORS: Record<string, ODataMethod> = Object.fromEntries(
  Object.entries(OPERATOR_ODATA_METHOD)
    .filter(([key]) => !NEGATED_OPERATOR_ODATA_METHOD.has(key as ODataMethod))
    .map(([key, value]) => [value, key as ODataMethod]),
);

type OdataDefinitionWith = NonNullable<OdataDefinition["with"]>;
